import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import AppShell from "../components/AppShell";
import { api, photoUrl } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Sparkles, Trash2, ArrowLeft, Pencil } from "lucide-react";
import { toast } from "sonner";

export default function EntryDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [entry, setEntry] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);

  const load = async () => {
    try { const { data } = await api.get(`/entries/${id}`); setEntry(data); }
    catch { toast.error("Could not load entry"); }
  };
  useEffect(() => { load(); }, [id]);

  const analyze = async () => {
    setAnalyzing(true);
    try {
      const { data } = await api.post(`/entries/${id}/analyze`);
      setEntry((e) => ({ ...e, ai_analysis: data.analysis }));
      toast.success("AI analysis complete");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Analysis failed");
    } finally { setAnalyzing(false); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this entry permanently?")) return;
    try { await api.delete(`/entries/${id}`); toast.success("Deleted"); navigate("/timeline"); }
    catch { toast.error("Failed to delete"); }
  };

  if (!entry) return <AppShell><p className="text-stone-500">Loading...</p></AppShell>;

  const sev = (v) => v <= 3 ? "bg-emerald-200 text-emerald-900" : v <= 6 ? "bg-amber-200 text-amber-900" : "bg-rose-200 text-rose-900";

  return (
    <AppShell>
      <Link to="/timeline" className="text-sm text-stone-500 hover:text-stone-800 flex items-center gap-1" data-testid="back-link"><ArrowLeft className="w-3 h-3"/>Back to timeline</Link>
      <div className="mt-3 flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-sage-500">{entry.entry_date}</p>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-stone-900 mt-1">{(entry.body_regions || []).join(" · ") || "Entry"}</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link to={`/entry/${id}/edit`}>
            <Button variant="outline" className="rounded-full border-stone-300" data-testid="edit-entry-btn">
              <Pencil className="w-4 h-4 mr-2"/>Edit
            </Button>
          </Link>
          <Button onClick={analyze} disabled={analyzing || (entry.photo_paths || []).length === 0} className="rounded-full bg-sage-400 hover:bg-sage-500 text-white" data-testid="analyze-btn">
            <Sparkles className="w-4 h-4 mr-2"/>{analyzing ? "Analyzing..." : entry.ai_analysis ? "Re-analyze" : "Analyze with AI"}
          </Button>
          <Button onClick={remove} variant="ghost" size="icon" className="text-stone-400 hover:text-rose-500" data-testid="delete-entry-btn"><Trash2 className="w-4 h-4"/></Button>
        </div>
      </div>

      <div className="mt-8 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {(entry.photo_paths || []).length > 0 && (
            <div className="bg-white rounded-2xl p-6 border border-stone-100" data-testid="photos-card">
              <h3 className="font-serif text-2xl text-stone-900">Photos</h3>
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-4">
                {(entry.photos?.length ? entry.photos : (entry.photo_paths || []).map(p => ({ path: p, body_regions: [] }))).map((ph, i) => (
                  <div key={i} className="space-y-2" data-testid={`detail-photo-${i}`}>
                    <img src={photoUrl(ph.path)} alt="" className="w-full h-44 object-cover rounded-xl"/>
                    {(ph.body_regions || []).length > 0 && (
                      <div className="flex flex-wrap gap-1" data-testid={`detail-photo-${i}-tags`}>
                        {ph.body_regions.map((r) => (
                          <Badge key={r} className="bg-sage-100 text-sage-700 border-0 rounded-full text-[10px] font-medium px-2 py-0.5">{r}</Badge>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {entry.ai_analysis && (
            <div className="bg-white rounded-2xl p-6 border border-stone-100" data-testid="ai-analysis-card">
              <div className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-clay-400"/><h3 className="font-serif text-2xl text-stone-900">AI observations</h3></div>
              <div className="mt-3 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-[12px] text-amber-900 leading-relaxed" data-testid="ai-disclaimer">
                <strong>This is not medical advice.</strong> AI output is informational only and may be inaccurate. Always consult a qualified dermatologist for any concerns.
              </div>
              <pre className="mt-4 whitespace-pre-wrap text-sm text-stone-700 font-sans leading-relaxed">{entry.ai_analysis}</pre>
            </div>
          )}

          {entry.notes && (
            <div className="bg-white rounded-2xl p-6 border border-stone-100">
              <h3 className="font-serif text-2xl text-stone-900">Notes</h3>
              <p className="mt-3 text-stone-600 leading-relaxed whitespace-pre-wrap">{entry.notes}</p>
            </div>
          )}

          {(entry.treatments || []).length > 0 && (
            <div className="bg-white rounded-2xl p-6 border border-stone-100">
              <h3 className="font-serif text-2xl text-stone-900">Treatments applied</h3>
              <ul className="mt-4 divide-y divide-stone-100">
                {entry.treatments.map((t, i) => (
                  <li key={i} className="py-3 flex justify-between items-start">
                    <div>
                      <p className="font-medium text-stone-900">{t.name}</p>
                      <p className="text-sm text-stone-500">{[t.dosage, t.applied_at].filter(Boolean).join(" · ")}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <aside>
          <div className="bg-white rounded-2xl p-6 border border-stone-100 sticky top-24">
            <h3 className="font-serif text-2xl text-stone-900">Severity</h3>
            <div className="mt-4 space-y-3">
              {(() => {
                const all = [["itching", "Itching"], ["pain", "Pain"], ["redness", "Redness"], ["dryness", "Dryness"], ["weeping", "Weeping"]];
                const enabled = entry.enabled_symptoms || ["itching", "pain", "redness"];
                const rows = all.filter(([k]) => enabled.includes(k));
                if (rows.length === 0) return <p className="text-sm text-stone-400 italic">No symptoms tracked.</p>;
                return rows.map(([k, label]) => (
                  <div key={k} className="flex justify-between items-center">
                    <span className="text-sm text-stone-600">{label}</span>
                    <Badge className={`${sev(entry[k] ?? 0)} border-0 rounded-full font-mono`}>{entry[k] ?? 0}/10</Badge>
                  </div>
                ));
              })()}
            </div>
          </div>
        </aside>
      </div>
    </AppShell>
  );
}
